import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PaperPlaneTilt, WhatsappLogo, EnvelopeSimple, Warning, Snowflake } from "@phosphor-icons/react";

const IS_MASK = (v) => typeof v === "string" && v.includes("•");

export default function Settings() {
  const [s, setS] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/settings/notifications"); setS(data); setDirty({}); }
    catch (e) { toast.error("Only admins can access settings"); }
  };
  useEffect(() => { load(); }, []);

  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const set = (k, v) => setDirty(d => ({ ...d, [k]: v }));
  const val = (k) => (dirty[k] !== undefined ? dirty[k] : s[k]);

  const save = async () => {
    setSaving(true);
    try {
      // Only send secrets if user actually edited them (i.e. no bullet mask)
      const payload = { ...dirty };
      for (const k of ["twilio_sid","twilio_token","resend_api_key","sendgrid_api_key"]) {
        if (IS_MASK(payload[k])) delete payload[k];
      }
      await api.post("/settings/notifications", payload);
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const testSend = async (channel) => {
    try {
      const { data } = await api.post("/settings/notifications/test", {
        channel, message: "Hello from Chericious ERP · test alert"
      });
      const per = (data.results || []).map(r =>
        r.status === "sent" ? `${r.kind} ✓`
        : r.status === "partial" ? `${r.kind} · partial`
        : `${r.kind} ✗ (${(r.errors?.[0]?.error || "failed").slice(0, 60)})`
      ).join(" · ");
      const ok = (data.results || []).length > 0 && (data.results || []).every(r => r.status === "sent");
      (ok ? toast.success : toast.error)(per || "No channel dispatched");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader subtitle="Admin · Notifications" title="External Alerts Configuration"
        actions={<Button onClick={save} disabled={saving || Object.keys(dirty).length === 0} data-testid="save-settings">Save changes</Button>}
      />

      <div className="p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-800 flex items-start gap-2 mb-4">
        <Warning size={16} className="mt-0.5"/>
        <div>
          Secrets are stored server-side and shown masked on reload. Leave a masked field untouched to keep the existing value.
          Secrets are stored server-side and shown masked on reload. Leave a masked field untouched to keep the existing value. Messages are queued into <span className="mono font-semibold">notification_outbox</span> and dispatched <span className="font-semibold">immediately</span> through the real Twilio (WhatsApp) and Emergent-managed (Email) transports on approval. Inspect delivery status at <span className="mono font-semibold">/api/notifications/outbox</span>.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* WhatsApp */}
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <WhatsappLogo size={22} weight="fill"/>
                </div>
                <div>
                  <div className="font-display font-bold text-lg tracking-tight">WhatsApp Alerts</div>
                  <div className="overline">Twilio API</div>
                </div>
              </div>
              <Switch checked={!!val("whatsapp_enabled")} onCheckedChange={v=>set("whatsapp_enabled", v)} data-testid="whatsapp-toggle"/>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="overline">Provider</Label>
                <Select value={val("whatsapp_provider") || "twilio"} onValueChange={v=>set("whatsapp_provider", v)}>
                  <SelectTrigger data-testid="whatsapp-provider"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twilio">Twilio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Twilio Account SID</Label>
                <Input value={val("twilio_sid") || ""} placeholder="AC..." onChange={e=>set("twilio_sid", e.target.value)} data-testid="twilio-sid"/>
                {s.has_twilio_sid && !dirty.twilio_sid && <div className="text-[10px] text-emerald-600 mt-1">✓ configured — edit to replace</div>}
              </div>
              <div>
                <Label className="overline">Twilio Auth Token</Label>
                <Input type="password" value={val("twilio_token") || ""} placeholder="•••" onChange={e=>set("twilio_token", e.target.value)} data-testid="twilio-token"/>
                {s.has_twilio_token && !dirty.twilio_token && <div className="text-[10px] text-emerald-600 mt-1">✓ configured — edit to replace</div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">From (WhatsApp)</Label>
                  <Input value={val("twilio_from") || ""} placeholder="whatsapp:+14155238886" onChange={e=>set("twilio_from", e.target.value)} data-testid="twilio-from"/>
                </div>
                <div>
                  <Label className="overline">Send To</Label>
                  <Input value={val("whatsapp_to") || ""} placeholder="+919876543210" onChange={e=>set("whatsapp_to", e.target.value)} data-testid="whatsapp-to"/>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
              {val("whatsapp_enabled") && val("whatsapp_to") ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Ready</Badge>
              ) : <Badge variant="secondary">Not configured</Badge>}
              <Button size="sm" variant="outline" onClick={()=>testSend("whatsapp")} disabled={!val("whatsapp_enabled") || !val("whatsapp_to")} data-testid="test-whatsapp">
                <PaperPlaneTilt size={14} className="mr-1"/>Send test
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email */}
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center">
                  <EnvelopeSimple size={22} weight="fill"/>
                </div>
                <div>
                  <div className="font-display font-bold text-lg tracking-tight">Email Alerts</div>
                  <div className="overline">Resend / SendGrid</div>
                </div>
              </div>
              <Switch checked={!!val("email_enabled")} onCheckedChange={v=>set("email_enabled", v)} data-testid="email-toggle"/>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="overline">Provider</Label>
                <Select value={val("email_provider") || "resend"} onValueChange={v=>set("email_provider", v)}>
                  <SelectTrigger data-testid="email-provider"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resend">Resend</SelectItem>
                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Resend API Key</Label>
                <Input type="password" value={val("resend_api_key") || ""} placeholder="re_..." onChange={e=>set("resend_api_key", e.target.value)} data-testid="resend-key"/>
                {s.has_resend_key && !dirty.resend_api_key && <div className="text-[10px] text-emerald-600 mt-1">✓ configured — edit to replace</div>}
              </div>
              <div>
                <Label className="overline">SendGrid API Key <span className="normal-case text-muted-foreground">(alternative)</span></Label>
                <Input type="password" value={val("sendgrid_api_key") || ""} placeholder="SG..." onChange={e=>set("sendgrid_api_key", e.target.value)} data-testid="sendgrid-key"/>
                {s.has_sendgrid_key && !dirty.sendgrid_api_key && <div className="text-[10px] text-emerald-600 mt-1">✓ configured — edit to replace</div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">From Email</Label>
                  <Input value={val("from_email") || ""} placeholder="alerts@chericious.com" onChange={e=>set("from_email", e.target.value)} data-testid="from-email"/>
                </div>
                <div>
                  <Label className="overline">Send To</Label>
                  <Input value={val("email_to") || ""} placeholder="ops@chericious.com" onChange={e=>set("email_to", e.target.value)} data-testid="email-to"/>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
              {val("email_enabled") && val("email_to") ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Ready</Badge>
              ) : <Badge variant="secondary">Not configured</Badge>}
              <Button size="sm" variant="outline" onClick={()=>testSend("email")} disabled={!val("email_enabled") || !val("email_to")} data-testid="test-email">
                <PaperPlaneTilt size={14} className="mr-1"/>Send test
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-none mt-4">
        <CardContent className="p-5">
          <div className="overline mb-3">How this connects to alerts</div>
          <ol className="text-sm space-y-1.5 text-muted-foreground list-decimal ml-5">
            <li>Every notification created by the ERP (QC failed, batch complete, vacuum alarm, delayed batch, etc.) is copied to <span className="mono">notification_outbox</span> if a channel is enabled and a destination is set.</li>
            <li>Approving a pending batch (or firing an auto-dispatched alert) immediately calls Twilio (WhatsApp) and the Emergent-managed email transport; the outbox row is stamped <span className="mono">sent</span>, <span className="mono">partial</span> or <span className="mono">failed</span> with the underlying error.</li>
            <li>Admins can inspect the queue at <span className="mono">GET /api/notifications/outbox</span>.</li>
          </ol>
        </CardContent>
      </Card>

      <FdThresholdCard/>
      <PlannerSettingsCard/>
    </div>
  );
}

function PlannerSettingsCard() {
  const [t, setT] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/planner").then(({ data }) => setT(data)).catch(()=>{});
  }, []);
  if (!t) return null;

  const val = (k) => (dirty[k] !== undefined ? dirty[k] : t[k]);
  const set = (k, v) => setDirty(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of Object.keys(dirty)) {
        if (dirty[k] === "" || dirty[k] === null) continue;
        payload[k] = Number(dirty[k]);
      }
      const { data } = await api.post("/settings/planner", payload);
      setT(data); setDirty({});
      toast.success("Planner rules saved");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Card className="shadow-none mt-4" data-testid="planner-settings-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display font-bold text-lg tracking-tight">Smart Planner Rules</div>
            <div className="overline">Demand-driven defaults · per-SKU overrides live on the SKU editor</div>
          </div>
          <Button size="sm" onClick={save} disabled={saving || Object.keys(dirty).length === 0} data-testid="save-planner-settings">
            Save rules
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label className="overline">Safety days (min stock = velocity × days)</Label>
            <Input type="number" step="1" value={val("safety_days") ?? ""}
                   onChange={e=>set("safety_days", e.target.value)}
                   data-testid="planner-safety-days" className="mono"/>
          </div>
          <div>
            <Label className="overline">Target days (target stock = velocity × days)</Label>
            <Input type="number" step="1" value={val("target_days") ?? ""}
                   onChange={e=>set("target_days", e.target.value)}
                   data-testid="planner-target-days" className="mono"/>
          </div>
          <div>
            <Label className="overline">Min-stock floor (pouches)</Label>
            <Input type="number" step="1" value={val("min_stock_floor") ?? ""}
                   onChange={e=>set("min_stock_floor", e.target.value)}
                   data-testid="planner-min-floor" className="mono"/>
          </div>
          <div>
            <Label className="overline">Target-stock floor (pouches)</Label>
            <Input type="number" step="1" value={val("target_stock_floor") ?? ""}
                   onChange={e=>set("target_stock_floor", e.target.value)}
                   data-testid="planner-target-floor" className="mono"/>
          </div>
          <div>
            <Label className="overline">Trend window (days)</Label>
            <Input type="number" step="1" value={val("trend_lookback_days") ?? ""}
                   onChange={e=>set("trend_lookback_days", e.target.value)}
                   data-testid="planner-trend-days" className="mono"/>
          </div>
          <div>
            <Label className="overline">Baseline window (days)</Label>
            <Input type="number" step="1" value={val("baseline_lookback_days") ?? ""}
                   onChange={e=>set("baseline_lookback_days", e.target.value)}
                   data-testid="planner-baseline-days" className="mono"/>
          </div>
        </div>
        <div className="mt-4 p-3 rounded-md border border-indigo-200 bg-indigo-50 text-xs text-indigo-900">
          Velocity blends recent (60%) &amp; baseline (40%) sales. When recent trends ±15% vs baseline the planner flags <b>Trending ↑/↓</b>. Per-SKU overrides (safety_stock, target_stock, sales_velocity_override) can be set on <span className="mono">SKUs &amp; Recipes → Planner</span>.
        </div>
      </CardContent>
    </Card>
  );
}

function FdThresholdCard() {
  const [t, setT] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/fd-thresholds").then(({ data }) => setT(data)).catch(()=>{});
  }, []);

  if (!t) return null;
  const val = (k) => (dirty[k] !== undefined ? dirty[k] : t[k]);
  const set = (k, v) => setDirty(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of Object.keys(dirty)) {
        const raw = dirty[k];
        if (raw === "" || raw === null) continue;
        payload[k] = Number(raw);
      }
      const { data } = await api.post("/settings/fd-thresholds", payload);
      setT(data); setDirty({});
      toast.success("Freeze-dryer thresholds saved");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Card className="shadow-none mt-4" data-testid="fd-threshold-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-sky-100 text-sky-700 flex items-center justify-center">
              <Snowflake size={22} weight="duotone"/>
            </div>
            <div>
              <div className="font-display font-bold text-lg tracking-tight">Freeze-Dry Cycle-End Thresholds</div>
              <div className="overline">Ready-to-End rule for FD-01 / FD-02</div>
            </div>
          </div>
          <Button size="sm" onClick={save} disabled={saving || Object.keys(dirty).length === 0} data-testid="save-fd-thresholds">
            Save thresholds
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="overline">Temperature Controller ≤ (°C)</Label>
            <Input type="number" step="0.1" value={val("temperature_controller_max_c") ?? ""}
                   onChange={e=>set("temperature_controller_max_c", e.target.value)}
                   data-testid="fd-tc-limit" className="mono"/>
          </div>
          <div>
            <Label className="overline">All Channels 1–8 ≤ (°C)</Label>
            <Input type="number" step="0.1" value={val("channel_max_c") ?? ""}
                   onChange={e=>set("channel_max_c", e.target.value)}
                   data-testid="fd-ch-limit" className="mono"/>
          </div>
          <div>
            <Label className="overline">Vacuum Level ≤ (mbar)</Label>
            <Input type="number" step="0.01" value={val("vacuum_max_mbar") ?? ""}
                   onChange={e=>set("vacuum_max_mbar", e.target.value)}
                   data-testid="fd-vac-limit" className="mono"/>
          </div>
          <div>
            <Label className="overline">Reminder interval (min)</Label>
            <Input type="number" step="1" value={val("reminder_interval_min") ?? ""}
                   onChange={e=>set("reminder_interval_min", e.target.value)}
                   data-testid="fd-reminder-limit" className="mono"/>
          </div>
        </div>

        <div className="mt-4 p-3 rounded-md border border-sky-200 bg-sky-50 text-xs text-sky-800">
          When an operator's reading meets <span className="font-semibold">all 10 conditions</span> (TC + 8 channels + vacuum), the freeze-dryer card shows the <span className="font-semibold">Ready to End</span> flag and unlocks <span className="font-semibold">Approve &amp; Notify</span>. Approval advances the batch <span className="mono">FreezeDrying → Packaging</span> and queues the manager alert.
        </div>
      </CardContent>
    </Card>
  );
}
